import React, { useCallback, useEffect } from 'react';
import { Formik } from 'formik';
import first from 'lodash/first';
import isEmpty from 'lodash/isEmpty';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { account } from '@redux/account';
import { Separator } from 'components/Separator';
import { Link } from 'components/Link/Link';
import { SignInWithApple } from 'components/Button/SignInWithApple';
import { Alert } from 'components/Alert';
import { Input } from 'components/Form/Input';
import { PasswordInput } from 'components/Form/PasswordInput';
import { Button } from 'components/Button/Button';
import { LanguageDropdown } from 'components/LanguageDropdown';
import { useDispatch } from 'utils/useDispatch';
import { useQuery } from 'utils/useQuery';

export const SignUp = () => {
  const { t } = useTranslation();
  let navigate = useNavigate();
  const getParams = useQuery();
  const location = useLocation();

  const accessToken = useSelector(account.selectors.accessToken);
  const hasError = useSelector(account.selectors.hasError);
  const errors = useSelector(account.selectors.errors);
  const firstError = first(errors);

  const register = useDispatch(account.actions.register);
  const fetchUser = useDispatch(account.actions.fetchUser);
  const setAccessToken = useDispatch(account.actions.setAccessToken);
  const clearErrors = useDispatch(account.actions.clearErrors);

  const handleNavigate = useCallback(() => {
    let { from } = location.state || { from: { pathname: "/app/playlists" } };
    fetchUser();
    navigate(from, { replace: true });
  }, [location.state, navigate, fetchUser]);

  useEffect(() => {
    if(!isEmpty(accessToken)) {
      setAccessToken(accessToken);
      return handleNavigate();
    }
  }, [accessToken, setAccessToken, handleNavigate]);

  useEffect(() => {
    return () => clearErrors();
  }, [clearErrors]);

  const handleRegister = useCallback(async ({ terms, ...values }, { setSubmitting }) => {
    await register(values);
    setSubmitting(false);
  }, [register]);

  return (
    <>
      <header className="flex flex-col space-y-5">
        <div className="relative text-center text-ui">
          <h1 className="text-xl font-semibold">{t('auth.signUp')}</h1>
          <LanguageDropdown className="absolute right-0 top-1/2 -translate-y-1/2 -mr-3 text-sm text-ui-3" />
        </div>
        {hasError && <Alert type="danger">{firstError}</Alert>}
        <SignInWithApple />
      </header>
      <Separator className="my-6">{t('common.or')}</Separator>
      <Formik
        initialValues={{
          name: '',
          email: '',
          password: '',
          password_confirmation: '',
          terms: false,
          query_params: getParams.toString(),
        }}
        onSubmit={handleRegister}
      >
        {({ values, handleChange, handleSubmit, isSubmitting }) => (
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Input
                className="w-full"
                size="small"
                type="text"
                name="name"
                autoComplete="name"
                required
                label={t('fields.userName')}
                placeholder={t('fields.namePlaceholder')}
                onChange={handleChange}
                value={values.name}
              />
            </div>
            <div>
              <Input
                className="w-full"
                size="small"
                type="email"
                name="email"
                autoComplete="email"
                required
                label={t('fields.email')}
                placeholder={t('fields.emailPlaceholder')}
                onChange={handleChange}
                value={values.email}
              />
            </div>
            <div>
              <PasswordInput
                className="w-full"
                size="small"
                name="password"
                autoComplete="new-password"
                required
                label={t('fields.password')}
                placeholder={t('fields.createPassword')}
                onChange={handleChange}
                value={values.password}
              />
            </div>
            <div>
              <PasswordInput
                className="w-full"
                size="small"
                name="password_confirmation"
                autoComplete="new-password"
                required
                label={t('fields.confirmPassword')}
                placeholder={t('fields.repeatPassword')}
                onChange={handleChange}
                value={values.password_confirmation}
              />
            </div>
            <label className="flex items-start gap-3 cursor-pointer text-ui-3 leading-snug">
              <input
                type="checkbox"
                name="terms"
                checked={values.terms}
                onChange={handleChange}
                required
                className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-ui-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-ui-accent-ring"
              />
              <span>
                {t('auth.agreePrefix')}{' '}
                <Link className="text-ui underline decoration-ui-3 underline-offset-4 hover:decoration-ui" href="https://uniplayer.net/terms/" target="_blank">{t('auth.termsLink')}</Link>
                {' '}{t('auth.agreeAnd')}{' '}
                <Link className="text-ui underline decoration-ui-3 underline-offset-4 hover:decoration-ui" href="https://uniplayer.net/privacy/" target="_blank">{t('auth.privacyLink')}</Link>.
              </span>
            </label>
            <Button
              type="submit"
              view="cta"
              isDisabled={isSubmitting || !values.terms}
              className="h-10 text-sm"
              center
              block
            >{t('auth.signUpButton')}</Button>
          </form>
        )}
      </Formik>
      <div className="mt-8 text-center text-ui-3">
        {t('auth.logInTitle')}
        <Link className="ml-2 text-ui underline decoration-ui-3 underline-offset-4 hover:decoration-ui" to="/auth/login">{t('auth.logIn')}</Link>
      </div>
    </>
  );
}
